export interface NavigationItem {
  routeName: string
  labelKey: string
  icon: string
  /** Every one of these is required. */
  permissions: string[]
  /**
   * ...and at least one of these, when present. A module is one entry even
   * when different roles reach it for different reasons: an approver and a
   * collector both belong in Solicitudes, and listing it twice made the menu
   * describe our permissions instead of their work.
   */
  anyPermissions?: string[]
  /**
   * Other routes this same entry stands for, so it stays lit wherever this
   * role actually lands. Vue Router's own `router-link-active` cannot answer
   * this: `/loan-requests/mine` only LOOKS nested — it is a sibling route
   * record, so the queue entry is never among the matched records and the
   * menu item the collector just tapped goes dark.
   */
  alsoActiveFor?: string[]
}

export interface NavigationGroup {
  key: string
  labelKey: string
  icon: string
  items: NavigationItem[]
}

/**
 * Single source of truth for the main menu, grouped to mirror the four
 * tabs the mobile app ships (Home / Applications / Loans / More). Someone
 * who learned the flow on the phone has to recognise it here.
 */
export const navigationGroups: NavigationGroup[] = [
  {
    key: 'home',
    labelKey: 'menu.groups.home',
    icon: 'pi pi-home',
    items: [
      {
        routeName: 'dashboard',
        labelKey: 'menu.dashboard',
        icon: 'pi pi-chart-bar',
        permissions: ['reports.view'],
      },
    ],
  },
  {
    key: 'applications',
    labelKey: 'menu.groups.applications',
    icon: 'pi pi-file-edit',
    items: [
      {
        routeName: 'loan-requests',
        labelKey: 'menu.loanRequests',
        icon: 'pi pi-inbox',
        permissions: [],
        anyPermissions: ['loanRequests.approve', 'loanRequests.create'],
        // Whoever may only FILE a request is sent to their own list by the
        // route guard; the entry has to follow them there.
        alsoActiveFor: ['my-loan-requests'],
      },
    ],
  },
  {
    key: 'loans',
    labelKey: 'menu.groups.loans',
    icon: 'pi pi-money-bill',
    items: [
      {
        // Loans, for everybody who may see one — a collector included.
        //
        // This used to demand `loans.list` so a collector never reached the
        // full book, and their world was a separate "Por cobrar" screen listing
        // CUOTAS that were already due. A client paying next week did not exist
        // to them: no visit, no early payment, no sight of what they answer for.
        //
        // The rows are narrowed server-side by IRouteScope either way, so the
        // wider permission opens a door, never somebody else's book.
        routeName: 'loans',
        labelKey: 'menu.loans',
        icon: 'pi pi-money-bill',
        permissions: ['loans.view'],
      },
      {
        routeName: 'customers',
        labelKey: 'menu.customers',
        icon: 'pi pi-users',
        permissions: ['customers.view'],
      },
      {
        routeName: 'my-payments',
        labelKey: 'menu.myPayments',
        icon: 'pi pi-receipt',
        permissions: ['payments.view'],
      },
    ],
  },
  {
    // Between loans and more: lending is the product this menu leads with,
    // and the books are read about it, not filed away as an afterthought.
    // The old single `accounting` entry is gone — its route is now a
    // permanent redirect to `finance-overview`, not a screen, so it carries
    // no menu entry of its own.
    key: 'finance',
    labelKey: 'menu.groups.finance',
    icon: 'pi pi-book',
    items: [
      {
        routeName: 'finance-overview',
        labelKey: 'menu.finance.overview',
        icon: 'pi pi-chart-line',
        permissions: ['reports.view'],
      },
      {
        routeName: 'finance-chart',
        labelKey: 'menu.finance.chart',
        icon: 'pi pi-book',
        permissions: ['reports.view'],
      },
      {
        routeName: 'finance-transactions',
        labelKey: 'menu.finance.transactions',
        icon: 'pi pi-list',
        permissions: ['reports.view'],
      },
      {
        routeName: 'finance-expenses',
        labelKey: 'menu.finance.expenses',
        icon: 'pi pi-minus-circle',
        permissions: ['reports.view'],
      },
      {
        routeName: 'finance-impairment',
        labelKey: 'menu.finance.impairment',
        icon: 'pi pi-shield',
        permissions: ['reports.view'],
      },
      {
        routeName: 'finance-period-close',
        labelKey: 'menu.finance.periodClose',
        icon: 'pi pi-lock',
        permissions: ['reports.view'],
      },
      {
        routeName: 'finance-posting-rules',
        labelKey: 'menu.finance.postingRules',
        icon: 'pi pi-sitemap',
        permissions: ['reports.view'],
      },
    ],
  },
  {
    key: 'more',
    labelKey: 'menu.groups.more',
    icon: 'pi pi-ellipsis-h',
    items: [
      {
        routeName: 'payment-review',
        labelKey: 'menu.paymentReview',
        icon: 'pi pi-flag',
        permissions: ['payments.review'],
      },
      {
        routeName: 'cash-close',
        labelKey: 'menu.cashClose',
        icon: 'pi pi-wallet',
        permissions: ['settings.manage'],
      },
      {
        routeName: 'commissions',
        labelKey: 'menu.commissions',
        icon: 'pi pi-percentage',
        permissions: ['settings.manage'],
      },
      {
        routeName: 'loan-tables',
        labelKey: 'menu.loanTables',
        icon: 'pi pi-table',
        permissions: ['loanTables.manage'],
      },
      {
        routeName: 'routes',
        labelKey: 'menu.routes',
        icon: 'pi pi-map',
        permissions: ['routes.manage'],
      },
      {
        routeName: 'members',
        labelKey: 'menu.members',
        icon: 'pi pi-id-card',
        permissions: ['users.manage'],
      },
      {
        routeName: 'import-backup',
        labelKey: 'menu.import',
        icon: 'pi pi-upload',
        permissions: ['migration.import'],
      },
    ],
  },
]

export const navigationItems: NavigationItem[] = navigationGroups.flatMap((group) => group.items)

/**
 * Deliberate, pre-existing exceptions to "every permission-gated route has a
 * menu entry" — each real today in `router/index.ts`, and none a hole
 * punched to make `routeReachability.spec.ts` pass. That test enforces the
 * requirement this list documents the exceptions to: "This project has
 * shipped an unreachable page once already."
 */
export const OFF_MENU = [
  // A nested detail route: `customers` is the menu destination, and this is
  // opened by clicking a row on it — never a menu entry of its own.
  'customer-detail',
  // Same shape, reached from `routes`.
  'route-detail',
  // Same shape again: `loans/:loanId` is a sibling route record in the
  // router, not a child, but it is reached the same way — a row on `loans`.
  'loan-detail',
  // Deliberately retired — see "no longer offers the cuota list to anybody"
  // below. Pre-existing debt this list records, not new debt.
  'collections-due',
  // Reached from the QR code on a provisional receipt, never from the menu.
  'verify-receipt',
]

/**
 * Whether a group is a header over a single entry, and so has nothing to
 * group.
 *
 * Solicitudes is exactly that: one module, inside a group carrying the same
 * word, so the menu printed "Solicitudes" twice in a row. Collapsing the two
 * entries into one (#811) left that pair behind.
 *
 * Decided on the SHAPE — one item — and never on the translated words. It used
 * to compare the two labels after translating them, which meant Spanish drew
 * three group headers and English four: the same menu changed structure when
 * somebody switched language, which reads as a bug because it is one. A group
 * of one has nothing to group in any language, so the header goes in all of
 * them.
 */
export const groupHoldsASingleEntry = (group: NavigationGroup): boolean =>
  group.items.length === 1

/**
 * What matters most to whoever is holding the phone, most specific first —
 * deliberately NOT the menu order. A collector holds `loanRequests.create`,
 * so ordering by the menu would drop them on "my requests" instead of the
 * street sequence they open the app for. Reordering the menu must not be
 * able to move this.
 *
 * Answers two questions that are really the same question: where a role
 * LANDS, and which destinations earn a permanent seat in the phone's bottom
 * bar (`mobileTabs`). Both ask "what does this person open the app for", so
 * they share one list rather than drifting apart.
 */
const importanceOrder = [
  'dashboard',
  // Lending is the product, and loans are now the screen a collector opens the
  // app for — the one that lists what they answer for and takes the payment.
  // It leads, where the cuota list used to.
  'loans',
  'loan-requests',
  'my-loan-requests',
  'customers',
  'my-payments',
  'payment-review',
  'loan-tables',
  'cash-close',
  'commissions',
  'routes',
  'members',
  'import-backup',
]

export const allowedNavigation = (
  hasAllPermissions: (required: string[]) => boolean,
): NavigationItem[] => navigationItems.filter((item) => isAllowed(item, hasAllPermissions))

const isAllowed = (
  item: NavigationItem,
  hasAllPermissions: (required: string[]) => boolean,
): boolean =>
  hasAllPermissions(item.permissions) &&
  (item.anyPermissions === undefined ||
    item.anyPermissions.some((permission) => hasAllPermissions([permission])))

export const allowedNavigationGroups = (
  hasAllPermissions: (required: string[]) => boolean,
): NavigationGroup[] =>
  navigationGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isAllowed(item, hasAllPermissions)),
    }))
    .filter((group) => group.items.length > 0)

export const firstAllowedRoute = (
  hasAllPermissions: (required: string[]) => boolean,
): string | null => {
  const allowed = new Set(allowedNavigation(hasAllPermissions).map((item) => item.routeName))
  return importanceOrder.find((routeName) => allowed.has(routeName)) ?? null
}

/**
 * The most slots a phone's bottom bar may hold. iOS and Android both stop at
 * five; past that the targets stop being thumb-sized and the bar stops being
 * scannable at a glance.
 */
const MAX_TABS = 5

export interface MobileTabs {
  /** Permanent seats in the bottom bar, most-used first. */
  tabs: NavigationItem[]
  /** What the "Más" sheet holds; empty when everything fit in the bar. */
  overflow: NavigationItem[]
}

/**
 * Splits what this user may reach into the phone's bottom bar and its "Más"
 * sheet — the webapp's answer to the TabBar the MAUI app ships, so someone
 * who learned the flow on the phone recognises it here.
 *
 * Ordered by {@link importanceOrder}, so the screen a person opens the app
 * for is the first tab rather than a consequence of menu grouping: a
 * collector gets *Por cobrar* one tap from anywhere, which is the entire
 * reason this replaced a drawer.
 *
 * Everything fits when there are five or fewer — spending a slot on "Más" to
 * hold a single entry is a tap for nothing. Past that the last slot becomes
 * the sheet, leaving four permanent seats.
 */
export const mobileTabs = (allowed: NavigationItem[]): MobileTabs => {
  const byImportance = [...allowed].sort(
    (left, right) => rank(left.routeName) - rank(right.routeName),
  )

  return byImportance.length <= MAX_TABS
    ? { tabs: byImportance, overflow: [] }
    : { tabs: byImportance.slice(0, MAX_TABS - 1), overflow: byImportance.slice(MAX_TABS - 1) }
}

// An entry nobody ranked sorts after every entry that was, rather than
// jumping the queue on a falsy index.
const rank = (routeName: string): number => {
  const index = importanceOrder.indexOf(routeName)
  return index === -1 ? Number.MAX_SAFE_INTEGER : index
}

/**
 * Whether this entry is the one the user is currently looking at — the menu's
 * own answer, not the router's. One module is one entry even when two roles
 * reach it through different routes, so the highlight has to cover every
 * route that entry stands for.
 */
/**
 * The part of an entry that navigation UI actually reads.
 *
 * The tenant menu carries permissions and the platform menu carries none —
 * the whole console sits behind one gate, so filtering its entries would hide
 * every one of them from the only person who may see them. Asking for the
 * narrower shape is what lets both use the same bar.
 */
export type NavigableItem = Pick<NavigationItem, 'routeName' | 'labelKey' | 'icon'> &
  Partial<Pick<NavigationItem, 'alsoActiveFor'>>

export const isNavigationItemActive = (
  item: Pick<NavigationItem, 'routeName'> & Partial<Pick<NavigationItem, 'alsoActiveFor'>>,
  currentRouteName: string | null | undefined,
): boolean =>
  currentRouteName != null &&
  (item.routeName === currentRouteName || (item.alsoActiveFor?.includes(currentRouteName) ?? false))
